import { motion } from "framer-motion";
import { Building2, PackageCheck, Handshake } from "lucide-react";
import { useReveal } from "@/components/marketing/Sections";
import { MENA, MENA_HIGHLIGHT } from "@/components/marketing/menaPaths";
import { WholesaleQuoteButton } from "@/components/marketing/WholesaleQuoteDialog";

const HUB = { x: 601, y: 266 };
const PINS = [
  { id: "libya", x: 500, y: 258, label: "Libya", side: -1 },
  { id: "iraq", x: 570, y: 236, label: "Iraq", side: -1 },
  { id: "oman", x: 611, y: 282, label: "Oman", side: 1 },
];

function MenaMap({ className = "" }) {
  return (
    <svg viewBox="440 180 240 160" className={className} role="img" aria-label="Wholesale supply across the Middle East and North Africa from Dubai" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="mn-hl" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#4F93E0" /><stop offset="1" stopColor="#245FAF" /></linearGradient>
        <filter id="mn-shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#245FAF" floodOpacity="0.35" /></filter>
      </defs>
      <g stroke="#D7E3F2" strokeWidth="0.4" opacity="0.7">
        {[480, 520, 560, 600, 640].map((x) => <line key={x} x1={x} y1="180" x2={x} y2="340" />)}
        {[210, 250, 290, 330].map((y) => <line key={y} x1="440" y1={y} x2="680" y2={y} />)}
      </g>
      <g fill="#C9DBF0" stroke="#fff" strokeWidth="0.5" strokeLinejoin="round">{MENA.map((d, i) => <path key={i} d={d} />)}</g>
      <g fill="url(#mn-hl)" stroke="#fff" strokeWidth="0.6" strokeLinejoin="round" filter="url(#mn-shadow)">{Object.values(MENA_HIGHLIGHT).map((d, i) => <path key={i} d={d} />)}</g>
      <g stroke="#F5C64D" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeDasharray="2.5 4">
        {PINS.map((p) => <path key={p.id} d={`M${HUB.x} ${HUB.y} Q ${(HUB.x + p.x) / 2} ${Math.min(HUB.y, p.y) - 30} ${p.x} ${p.y}`} />)}
      </g>
      {PINS.map((p) => {
        const w = p.label.length * 4.4 + 10;
        const x = p.side < 0 ? -w - 6 : 6;
        return (
          <g key={p.id} transform={`translate(${p.x} ${p.y})`}>
            <circle r="6" fill="#F5C64D" opacity="0.3" />
            <circle r="2.6" fill="#fff" stroke="#0D1B2A" strokeWidth="1.2" />
            <rect x={x} y="-6" width={w} height="12" rx="6" fill="#fff" stroke="#D7E3F2" strokeWidth="0.5" />
            <text x={x + w / 2} y="2.3" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="6" fontWeight="600" fill="#0D1B2A">{p.label}</text>
          </g>
        );
      })}
      <g transform={`translate(${HUB.x} ${HUB.y})`}>
        <circle r="7" fill="#F5C64D" opacity="0.3" /><circle r="3.6" fill="#F5C64D" /><circle r="1.4" fill="#0D1B2A" />
      </g>
      <g transform="translate(612 296)">
        <rect width="54" height="12" rx="6" fill="#0D1B2A" />
        <text x="27" y="8.3" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="6" fontWeight="700" fill="#fff">Dubai DMCC hub</text>
      </g>
    </svg>
  );
}

const MENA_COUNTRIES = ["Libya", "Iraq", "Oman", "UAE", "Saudi Arabia", "Kuwait", "Qatar", "Bahrain", "Jordan", "Lebanon", "Egypt", "Yemen"];

export function Mena() {
  const reveal = useReveal();
  return (
    <section id="mena" className="scroll-mt-20 bg-white py-20 lg:py-28" data-testid="mena-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        <motion.div {...reveal(0)} className="order-2 lg:order-1 lg:col-span-6">
          <div className="relative rounded-3xl border border-[#D7E3F2] bg-gradient-to-b from-[#EAF2FB] to-white p-6 shadow-[0_30px_60px_-30px_rgba(13,27,42,0.25)]">
            <MenaMap className="w-full" />
          </div>
        </motion.div>
        <div className="order-1 lg:order-2 lg:col-span-6">
          <motion.div {...reveal(0)}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#377DD3]">
              <span className="h-px w-6 bg-[#F5C64D]" /> Middle East & North Africa
            </span>
          </motion.div>
          <motion.h2 {...reveal(0.06)} className="font-heading mt-4 text-3xl font-bold tracking-tight text-[#0D1B2A] sm:text-4xl">
            Supporting wholesalers across the MENA region.
          </motion.h2>
          <motion.p {...reveal(0.1)} className="mt-5 text-base leading-relaxed text-[#64748B] sm:text-lg">
            From our Dubai DMCC hub we supply licensed pharmaceutical wholesalers and distributors
            across the Middle East and North Africa — including Libya, Iraq and Oman — with
            consistent access to originator products, reliable lead times and cold-chain shipping
            to their warehouse.
          </motion.p>
          <div className="mt-8 space-y-4">
            {[
              { icon: Building2, t: "Wholesaler-first supply", d: "Bulk and repeat orders for national and regional distributors, with multi-currency invoicing." },
              { icon: PackageCheck, t: "Consistent availability", d: "Multi-warehouse stock in Dubai keeps essential and specialty lines available for fast re-supply." },
              { icon: Handshake, t: "Long-term partnerships", d: "Dedicated account management, trade-credit support and shipment tracking in the portal." },
            ].map((p, i) => (
              <motion.div key={p.t} {...reveal(0.12 + i * 0.06)} className="flex gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#377DD3] to-[#245FAF] text-white shadow-md"><p.icon size={20} /></div>
                <div>
                  <h3 className="font-semibold text-[#0D1B2A]">{p.t}</h3>
                  <p className="mt-1 text-sm text-[#64748B]">{p.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div {...reveal(0.3)} className="mt-8 flex flex-wrap gap-2" data-testid="mena-country-list">
            {MENA_COUNTRIES.map((c) => (
              <span key={c} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium ${["Libya", "Iraq", "Oman"].includes(c) ? "border-[#F5C64D] bg-[#F5C64D]/15 text-[#0D1B2A]" : "border-[#D7E3F2] bg-[#F8FAFC] text-[#0D1B2A]"}`}>{c}</span>
            ))}
          </motion.div>
          <motion.div {...reveal(0.34)} className="mt-8">
            <WholesaleQuoteButton region="MENA" countries={MENA_COUNTRIES} testid="mena-quote-button" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
